import { __assign, __read, __spread } from "tslib";
import React, { forwardRef, useContext, useCallback, } from "react";
import classNames from "classnames";
import { Text } from "../text";
import { TableBox } from "./TableBox";
import { useMiddleware } from "./util/use-middleware";
import { isCallable } from "../_util/is-callable";
import { useConfig } from "../_util/config-context";
import { getFixedOffset, getFixedEndColumn, CellResizeObserver, } from "./TableBody";
import { TableContext } from "./TableContext";
import { DragContext } from "./addons/draggable/DraggableTable";
import { ResizeContext } from "./addons/resizable/ResizableTable";
export var TableHead = forwardRef(function TableHead(props, ref) {
    var classPrefix = useConfig().classPrefix;
    var _a = useContext(TableContext), scrollStatus = _a.scrollStatus, columnsWidths = _a.columnsWidths;
    var _b = useContext(DragContext), dragEventHandlers = _b.dragEventHandlers, draggableColumns = _b.draggableColumns;
    var isResizing = useContext(ResizeContext).isResizing;
    var columns = props.columns, records = props.records, addons = props.addons, tableBoxStyle = props.tableBoxStyle, disableTextOverflow = props.disableTextOverflow, hideHeader = props.hideHeader, virtualizedOptions = props.virtualizedOptions, headerRows = props.headerRows, columnsDraggable = props.columnsDraggable, columnsResizable = props.columnsResizable, onResize = props.onResize;
    var hasFixedColumn = columns.find(function (column) { return !!column.fixed; });
    var renderColumn = useMiddleware(addons, "onInjectColumn")(function (record, rowKey, recordIndex, column, columnIndex) {
        var key = column.key, header = column.header;
        var children = header;
        // pass a render function
        if (isCallable(header)) {
            children = header(column);
        }
        // forgot to pass, use a key as a reminder
        if (header === undefined) {
            children = key;
        }
        // plain values
        if (typeof children === "string" || typeof children === "number") {
            children = (React.createElement(Text, { overflow: !disableTextOverflow, title: disableTextOverflow ? undefined : String(children) }, children));
        }
        return {
            props: {},
            children: children,
        };
    });
    var tableHeaders = columns.map(function (column, index) {
        var _a, _b;
        var _c = renderColumn(null, null, -1, column, index), props = _c.props, children = _c.children;
        var cKey = column.key;
        if (props.colSpan === 0) {
            return null;
        }
        if (virtualizedOptions) {
            props.style = __assign(__assign({}, (props.style || {})), { display: "inline-block", overflow: "hidden", boxSizing: "border-box", flex: column.width ? undefined : 1, flexBasis: column.width || 1 });
        }
        // 固定列
        if ((column.fixed === "left" || column.fixed === "right") &&
            column.fixed !== scrollStatus &&
            scrollStatus !== "no-scroll") {
            var end = getFixedEndColumn(columns);
            props.className = classNames(props.className, classPrefix + "-table__td--fixed", (_a = {},
                _a[classPrefix + "-table__td--fixed-" + column.fixed] = end[column.fixed] === index,
                _a));
            props.style = __assign(__assign({}, (props.style || {})), (_b = { position: "sticky" }, _b[column.fixed] = getFixedOffset(columns, index, columnsWidths, column.fixed), _b));
        }
        // 列拖拽排序
        if (!column.fixed && columnsDraggable && !("ignore" in column)) {
            var onDragEnter = dragEventHandlers.onDragEnter, onDragOver = dragEventHandlers.onDragOver, onDrop = dragEventHandlers.onDrop, onDragStart = dragEventHandlers.onDragStart, onDrag = dragEventHandlers.onDrag, onDragLeave = dragEventHandlers.onDragLeave, onDragEnd = dragEventHandlers.onDragEnd, onMouseEnter = dragEventHandlers.onMouseEnter, onMouseLeave = dragEventHandlers.onMouseLeave;
            props["data-column"] = column.key;
            props.onDragEnter = onDragEnter;
            props.onDragOver = onDragOver;
            props.onDrop = onDrop;
            if (draggableColumns === "all" || draggableColumns.includes(cKey)) {
                props.draggable = true;
                props.onDragStart = onDragStart;
                props.onDrag = onDrag;
                props.onDragLeave = onDragLeave;
                props.onDragEnd = onDragEnd;
                props.onMouseEnter = onMouseEnter;
                props.onMouseLeave = onMouseLeave;
                props.className = classNames(props.className, "is-draggable");
            }
        }
        var childrenStyle = columnsResizable && isResizing ? { pointerEvents: "none" } : {};
        var cell = (React.createElement(Text, __assign({ key: cKey, parent: "th" }, props, { align: column.align }),
            React.createElement("div", { style: childrenStyle }, children)));
        // 无数据时通过头确定列宽
        if ((records || []).length === 0 && hasFixedColumn) {
            return (React.createElement(CellResizeObserver, { key: "" + cKey + index, onResize: function (width) {
                    onResize(function (widths) {
                        var columnsWidths = __spread(widths);
                        columnsWidths[index] = width;
                        return columnsWidths;
                    });
                } }, cell));
        }
        return cell;
    });
    var renderGroupingHeader = useCallback(function (heads) {
        return headerRows.map(function (row) { return (React.createElement("tr", { key: row.map(function (i) { return i.key; }).join("-") }, row.map(function (_a) {
            var key = _a.key, isLeaf = _a.isLeaf, title = _a.title, _b = _a.align, align = _b === void 0 ? "left" : _b, colSpan = _a.colSpan;
            if (isLeaf) {
                var index = columns.findIndex(function (i) { return i.key === key; });
                return heads[index];
            }
            return (React.createElement(Text, { key: key, parent: "th", colSpan: colSpan, align: align },
                React.createElement("div", null, title)));
        }))); });
    }, [columns, headerRows]);
    return useMiddleware(addons, "onInjectHead")(function (props) { return (React.createElement("div", { className: classPrefix + "-table__header", ref: ref, style: hideHeader ? { display: "none" } : undefined },
        React.createElement(TableBox, { columns: columns, style: __assign(__assign({}, (tableBoxStyle || {})), { display: virtualizedOptions ? "block" : undefined }), classPrefix: classPrefix },
            React.createElement("thead", { style: virtualizedOptions ? { display: "block" } : undefined }, headerRows ? (renderGroupingHeader(tableHeaders)) : (React.createElement("tr", { style: virtualizedOptions ? { display: "flex" } : undefined }, tableHeaders)))))); })(props);
});
TableHead.displayName = "TableHead";
//# sourceMappingURL=TableHead.js.map